<template>
  <v-app>
    <v-container fluid>
      <v-row>
        <v-col cols="12">
          <v-expansion-panels v-model="panel">
            <v-expansion-panel>
              <v-expansion-panel-title
                color="blue-grey-lighten-4"
                class="panel-header"
                >Search Form</v-expansion-panel-title
              >
              <v-expansion-panel-text>
                <v-form @submit.prevent="submit">
                  <v-row class="px-2 pt-4 pb-0">
                    <v-col lg="2" md="2" cols="12">
                      <v-date-input
                        input-format="dd/mm/yyyy"
                        label="From Date"
                        density="compact"
                        prepend-icon=""
                        prepend-inner-icon="mdi mdi-calendar-month"
                        single-line
                        variant="outlined"
                        bg-color="grey-lighten-4"
                        v-model="from_date.value.value"
                        :error-messages="from_date.errorMessage.value"
                        autocomplete="off"
                        clearable
                      ></v-date-input>
                    </v-col>

                    <v-col lg="2" md="2" cols="12">
                      <v-date-input
                        input-format="dd/mm/yyyy"
                        label="To Date"
                        density="compact"
                        single-line
                        prepend-icon=""
                        prepend-inner-icon="mdi mdi-calendar-month"
                        variant="outlined"
                        bg-color="grey-lighten-4"
                        v-model="to_date.value.value"
                        :error-messages="to_date.errorMessage.value"
                        autocomplete="off"
                        clearable
                      ></v-date-input>
                    </v-col>

                    <v-col lg="3" md="3" cols="12">
                      <v-autocomplete
                        variant="outlined"
                        density="compact"
                        bg-color="grey-lighten-4"
                        v-model="employee_id.value.value"
                        :items="getHrmEmployeeList"
                        :item-title="(item) => `${item.name} - ${item.code || item.employee_code || item.emp_id || ''}`"
                        item-value="id"
                        :error-messages="employee_id.errorMessage.value"
                        autocomplete="off"
                        clearable
                        :loading="loading"
                        no-data-text="No employees found"
                        placeholder="Select Employee"
                      >
                        <template #label>
                          Select Employee
                        </template>
                      </v-autocomplete>
                    </v-col>

                    <v-col lg="2" md="2" cols="12">
                      <v-btn
                        class="bg-brand w-15"
                        type="submit"
                        ref="searchButton"
                        >Search</v-btn
                      >
                    </v-col>
                  </v-row>
                </v-form>
              </v-expansion-panel-text>
            </v-expansion-panel>
          </v-expansion-panels>
        </v-col>
      </v-row>
    </v-container>

    <v-container fluid style="margin-top: -20px;">
      <v-row>
        <v-col cols="12">
          <v-expansion-panels v-model="panel">
            <v-expansion-panel>
              <v-expansion-panel-title
                color="blue-grey-lighten-4"
                class="panel-header"
                >Money Requisition List</v-expansion-panel-title
              >
              <v-expansion-panel-text>
                <v-row class="py-2">
                  <v-col md="4" cols="12">
                    <v-text-field
                      v-model="search"
                      append-inner-icon="mdi-magnify"
                      label="Search"
                      single-line
                      hide-details
                      density="compact"
                      variant="outlined"
                      bg-color="grey-lighten-4"
                      class="pl-2"
                      clearable
                    ></v-text-field>
                  </v-col>

                  <v-col md="5" cols="12">
                    <div class="d-flex align-center flex-column">
                      <v-btn-group
                        variant="outlined"
                        density="compact"
                        size="x-small"
                        divided
                      >
                        <v-btn
                          prepend-icon="mdi mdi-download"
                          variant="flat"
                          color="success"
                          @click="exportToCSV"
                          >CSV</v-btn
                        >
                        <v-btn
                          prepend-icon="mdi mdi-download"
                          variant="flat"
                          color="primary"
                          @click="exportToExcel"
                          >EXCEL</v-btn
                        >
                        <v-btn
                          prepend-icon="mdi mdi-download"
                          variant="flat"
                          color="red"
                          @click="exportToPDF"
                          >PDF</v-btn
                        >
                      </v-btn-group>
                    </div>
                  </v-col>

                  <v-col md="3" cols="12">
                    <div class="text-right pr-2">
                      <v-btn
                        v-if="$insertAccess($route.fullPath)"
                        @click="goTo('/account/add-money-requisition')"
                        color="primary"
                        variant="tonal"
                        prepend-icon="mdi mdi-plus"
                        rounded="xl"
                      >
                        Create Requisition
                      </v-btn>
                    </div>
                  </v-col>
                </v-row>
                <v-data-table
                  :headers="headers"
                  :items="getSchema"
                  :loading="loading"
                  v-model:items-per-page="itemsPerPage"
                  :search="search"
                  :hover="true"
                  density="compact"
                  show-expand
                  item-key="id"
                >
                  <template v-slot:item.index="{ index }">
                    {{ ++index }}
                  </template>

                  <template v-slot:item.code="{ item }">
                    <span
                      class="text-primary cursor-pointer over:underline font-medium"
                      @click="viewRequisition(item)"
                    >
                      {{ item.code }}
                    </span>
                  </template>

                  <template v-slot:item.total_amount="{ item }">
                    {{ formatCurrency(item.total_amount) }}
                  </template>

                  <template v-slot:item.due_amount="{ item }">
                    {{ formatCurrency(item.due_amount) }}
                  </template>

                  <template v-slot:item.disburse_amount="{ item }">
                    {{ formatCurrency(item.disburse_amount || 0) }}
                  </template>

                  <template v-slot:item.settle_amount="{ item }">
                    {{ formatCurrency(item.settle_amount || 0) }}
                  </template>

                  <template v-slot:item.approval_status_display="{ item }">
                    <v-chip
                      v-if="item.approval_current_process && item.approval_current_process.status === 'Pending'"
                      color="yellow-darken-3"
                      size="small"
                    >
                      Wating for {{ item.approval_current_process.approval_step_name?.name || 'N/A' }}
                    </v-chip>

                    <v-chip
                      v-else-if="item.approval_current_process && item.approval_current_process.status === 'Approved'"
                      color="green"
                      size="small"
                    >
                      Approved By {{ item.approval_current_process.approval_step_name?.name || 'N/A' }}
                    </v-chip>
                    <v-chip
                      v-else-if="item.approval_current_process && item.approval_current_process.status === 'Cancelled'"
                      color="red"
                      size="small"
                    >
                      Cancelled By {{ item.approval_current_process.approval_step_name?.name || 'N/A' }}
                    </v-chip>
                    <v-chip
                      v-else
                      color="green"
                      size="small"
                    >
                      {{ item.approval_status || 'N/A' }}
                    </v-chip>
                  </template>



                  <template v-slot:item.status="{ item }">
                    <v-chip
                      :color="item.status == 1 ? 'success' : 'grey'"
                      size="small"
                    >
                      {{ item.status == 1 ? "Active" : "Inactive" }}
                    </v-chip>
                  </template>

                  <template v-slot:item.actions="{ item }">
                    <div class="d-flex align-center justify-center" style="gap: 4px;">
                      <v-btn
                        icon
                        size="small"
                        color="primary"
                        variant="text"
                        @click="editItem(item)"
                        v-if="canEdit(item)"
                        title="Edit"
                      >
                        <v-icon>mdi-pencil</v-icon>
                      </v-btn>
                      <v-btn
                        icon
                        size="small"
                        color="success"
                        variant="text"
                        @click="openDisburseModal(item)"
                        v-if="canDisburse(item)"
                        title="Disburse"
                      >
                        <v-icon>mdi-cash-multiple</v-icon>
                      </v-btn>
                      <v-btn
                        icon
                        size="small"
                        color="info"
                        variant="text"
                        @click="viewRequisition(item)"
                        title="View Details"
                      >
                        <v-icon>mdi-eye</v-icon>
                      </v-btn>
                    </div>
                  </template>

                  <!-- Expanded Row for Settlement Details -->
                  <template v-slot:expanded-row="{ item, columns }">
                    <tr>
                      <td :colspan="columns.length">
                        <v-card flat class="ma-2 pa-2 rounded" color="grey-lighten-4">
                          <div class="text-subtitle-2 text-center text-decoration-underline mb-2">
                            Advance Settlement Details
                          </div>
                          <v-data-table
                            :headers="settlementHeaders"
                            :items="(item.advanceSettles || item.advance_settles || [])"
                            density="compact"
                            class="elevation-1 pa-2 rounded"
                            hide-default-footer
                            :items-per-page="-1"
                          >
                            <template v-slot:item.index="{ index }">
                              {{ index + 1 }}
                            </template>

                            <template v-slot:item.code="{ item: settlement }">
                              <span class="text-primary font-medium">
                                {{ settlement.code || 'N/A' }}
                              </span>
                            </template>

                            <template v-slot:item.settle_date="{ item: settlement }">
                              {{ formattedDate(settlement.settle_date) }}
                            </template>

                            <template v-slot:item.settle_amount="{ item: settlement }">
                              {{ formatCurrency(settlement.settle_amount || 0) }}
                            </template>

                            <template v-slot:item.approval_status_display="{ item: settlement }">
                              <v-chip
                                v-if="settlement.approval_current_process && settlement.approval_current_process.status === 'Pending'"
                                color="yellow-darken-3"
                                size="small"
                              >
                                Wating for {{ settlement.approval_current_process.approval_step_name?.name || 'N/A' }}
                              </v-chip>

                              <v-chip
                                v-else-if="settlement.approval_current_process && settlement.approval_current_process.status === 'Approved'"
                                color="green"
                                size="small"
                              >
                                Approved By {{ settlement.approval_current_process.approval_step_name?.name || 'N/A' }}
                              </v-chip>
                              <v-chip
                                v-else-if="settlement.approval_current_process && settlement.approval_current_process.status === 'Cancelled'"
                                color="red"
                                size="small"
                              >
                                Cancelled By {{ settlement.approval_current_process.approval_step_name?.name || 'N/A' }}
                              </v-chip>
                              <v-chip
                                v-else
                                color="green"
                                size="small"
                              >
                                {{ settlement.approval_status || 'N/A' }}
                              </v-chip>
                            </template>

                            <template v-slot:no-data>
                              <div class="text-center pa-4">
                                <p class="text-grey">No settlement records found</p>
                              </div>
                            </template>
                          </v-data-table>
                        </v-card>
                      </td>
                    </tr>
                  </template>
                </v-data-table>
              </v-expansion-panel-text>
            </v-expansion-panel>
          </v-expansion-panels>
        </v-col>
      </v-row>
    </v-container>

    <!-- Disbursement Modal -->
    <v-dialog v-model="disburseDialog" max-width="600px" persistent>
      <v-card>
        <v-card-title class="bg-primary text-white">
          <span class="text-h6">Disburse Money Requisition</span>
          <v-spacer></v-spacer>
         
        </v-card-title>

        <v-card-text class="pa-4">
          <v-form ref="disburseForm" @submit.prevent="submitDisbursement">
            <v-row>
              <v-col cols="12">
                <v-alert type="info" density="compact" class="mb-4">
                  <div><strong>Code:</strong> {{ selectedRequisition?.code }}</div>
                  <div><strong>Due Amount:</strong> {{ formatCurrency(selectedRequisition?.due_amount || 0) }}</div>
                </v-alert>
              </v-col>

              <v-col cols="12" md="6">
                <v-date-input
                  input-format="dd/mm/yyyy"
                  v-model="disbursement_date.value.value"
                  label="Disbursement Date *"
                  variant="outlined"
                  density="compact"
                  prepend-icon=""
                  prepend-inner-icon="mdi-calendar"
                  :error-messages="disbursement_date.errorMessage.value"
                ></v-date-input>
              </v-col>

              <v-col cols="12" md="6">
                <v-text-field
                  v-model="paid_amount.value.value"
                  label="Paid Amount *"
                  variant="outlined"
                  density="compact"
                  type="number"
                  min="0.01"
                  step="0.01"
                  prepend-inner-icon="mdi-currency-usd"
                  :error-messages="paid_amount.errorMessage.value"
                  :max="selectedRequisition?.due_amount || 0"
                ></v-text-field>
              </v-col>

              <v-col cols="12" md="6">
                <v-select
                  v-model="payment_method.value.value"
                  :items="paymentMethods"
                  item-title="text"
                  item-value="value"
                  label="Payment Method *"
                  variant="outlined"
                  density="compact"
                  prepend-inner-icon="mdi-cash-multiple"
                  :error-messages="payment_method.errorMessage.value"
                  @update:model-value="onPaymentMethodChange"
                ></v-select>
              </v-col>

              <v-col cols="12" md="6">
                <v-autocomplete
                  v-model="payment_acc_account_head_id.value.value"
                  :items="getPaymentAccountHeadList"
                  :item-title="(item) => item.name || 'Select'"
                  item-value="id"
                  label="Payment Account Head *"
                  variant="outlined"
                  density="compact"
                  prepend-inner-icon="mdi-bank"
                  :error-messages="payment_acc_account_head_id.errorMessage.value"
                  :loading="loadingAccountHeads"
                  clearable
                ></v-autocomplete>
              </v-col>

              <!-- Bank Details (Conditional) -->
              <template v-if="payment_method.value.value === 'BANK'">
                <v-col cols="12" md="6">
                  <v-text-field
                    v-model="cheque_no.value.value"
                    label="Cheque No *"
                    variant="outlined"
                    density="compact"
                    prepend-inner-icon="mdi-checkbook"
                    :error-messages="cheque_no.errorMessage.value"
                    maxlength="30"
                  ></v-text-field>
                </v-col>

                <v-col cols="12" md="6">
                  <v-date-input
                    input-format="dd/mm/yyyy"
                    v-model="cheque_date.value.value"
                    label="Cheque Date *"
                    variant="outlined"
                    density="compact"
                    prepend-icon=""
                    prepend-inner-icon="mdi-calendar"
                    :error-messages="cheque_date.errorMessage.value"
                  ></v-date-input>
                </v-col>
              </template>

              <!-- Mobile Banking Details (Conditional) -->
              <template v-if="payment_method.value.value === 'MOBILE_BANKING'">
                <v-col cols="12" md="6">
                  <v-autocomplete
                    v-model="mobileProvider.value.value"
                    :items="mobileProviders"
                    item-title="label"
                    item-value="value"
                    label="Provider *"
                    variant="outlined"
                    density="compact"
                    prepend-inner-icon="mdi-cellphone"
                    :error-messages="mobileProvider.errorMessage.value"
                    clearable
                  ></v-autocomplete>
                </v-col>

                <v-col cols="12" md="6">
                  <v-text-field
                    v-model="transactionId.value.value"
                    label="Transaction ID *"
                    variant="outlined"
                    density="compact"
                    prepend-inner-icon="mdi-identifier"
                    :error-messages="transactionId.errorMessage.value"
                    maxlength="50"
                  ></v-text-field>
                </v-col>
              </template>

              <v-col cols="12">
                <v-textarea
                  v-model="remarks.value.value"
                  label="Remarks"
                  variant="outlined"
                  density="compact"
                  rows="3"
                  :error-messages="remarks.errorMessage.value"
                ></v-textarea>
              </v-col>
            </v-row>
          </v-form>
        </v-card-text>

        <v-card-actions class="pa-4">
          <v-spacer></v-spacer>
          <v-btn
            color="grey"
            variant="text"
            @click="closeDisburseModal"
            :disabled="disbursing"
          >
            Cancel
          </v-btn>
          <v-btn
            color="primary"
            variant="flat"
            @click="submitDisbursement"
            :loading="disbursing"
            :disabled="disbursing"
          >
            Disburse
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>
  </v-app>
</template>

<script>
import { useStore } from "vuex";
import { useField, useForm } from "vee-validate";
import * as yup from "yup";
import { ref } from "vue";
import { toast } from "vue3-toastify";
import formattedDate from "@/plugins/dateFormatter";
import { useRouter } from "vue-router";
import statusMap from "@/utils/statusMap";

export default {
  components: {},
  data: () => ({
    itemsPerPage: 10,
    panel: [0],
    loading: false,
    search: "",
    dialog: false,
    data: {},
    statusMap,
    disburseDialog: false,
    selectedRequisition: null,
    disbursing: false,
    loadingAccountHeads: false,
    paymentMethods: [
      { text: "Cash", value: "CASH" },
      { text: "Bank", value: "BANK" },
      { text: "Mobile Banking", value: "MOBILE_BANKING" },
    ],
    mobileProviders: [
      { label: "Bkash", value: "bkash" },
      { label: "Nagad", value: "nagad" },
      { label: "Rocket", value: "rocket" },
    ],
    headers: [
      { title: "#SL", align: "center", key: "index", width: "7%", sortable: false },
      { title: "Code", key: "code", align: "center", sortable: false },
      { title: "Employee", key: "employee.name", align: "left", sortable: false },
      { title: "Purpose", align: "left", key: "purpose", sortable: false },
      { title: "Total Amount", align: "end", key: "total_amount", sortable: true },
      {title: "Due Amount", align: "end", key: "due_amount", sortable: true },
      {title: "Disburse Amount", align: "end", key: "disburse_amount", sortable: true },
      {title: "Settle Amount", align: "end", key: "settle_amount", sortable: true },
      {title: "Approval Status", align: "center", key: "approval_status_display", sortable: false },
      { title: "Status", align: "center", key: "status", sortable: true },
      { title: "Actions", align: "center", key: "actions", sortable: false },
    ],
    settlementHeaders: [
      { title: "#SL", align: "center", key: "index", width: "5%", sortable: false },
      { title: "Settle Code", key: "code", align: "left", sortable: false },
      { title: "Settle Date", key: "settle_date", align: "center", sortable: false },
      { title: "Settle Amount", align: "end", key: "settle_amount", sortable: false },
      { title: "Approval Status", align: "center", key: "approval_status_display", sortable: false },
    ],
  }),
  watch: {},
  created() {
    if (this.$store.getters.getHrmEmployeeList.length === 0) {
      this.$store.dispatch("getHrmEmployeeList");
    }
    // Reset form to ensure employee field starts empty
    this.resetForm();
    this.loadData();
  },
  computed: {
    getSchema() {
      return this.$store.getters.getMoneyRequisitionList;
    },
    getHrmEmployeeList() {
      return this.$store.getters.getHrmEmployeeList;
    },
    getPaymentAccountHeadList() {
      return this.$store.getters.getPaymentAccountHeadList || [];
    },
  },
  setup() {
    const { handleSubmit, resetForm } = useForm({
      validationSchema: yup.object({
        from_date: yup.date().nullable().label("From Date"),
        to_date: yup.date().nullable().label("To Date"),
        employee_id: yup.number().positive().integer().nullable().label("Employee"),
      }),
    });

    const from_date = useField("from_date");
    const to_date = useField("to_date");
    const employee_id = useField("employee_id");
    const loading = ref(false);
    const store = useStore();
    const router = useRouter();

    const submit = handleSubmit((values) => {
      loadData();
    });

    const loadData = () => {
      loading.value = true;
      store
        .dispatch("getMoneyRequisitionList")
        .then(() => {
          // Data loaded successfully
        })
        .catch((error) => {
          toast.error(error.response?.data?.message || "Failed to load money requisitions");
        })
        .finally(() => {
          loading.value = false;
        });
    };

    // Disbursement form - validation will be handled in submitDisbursement method
    const disbursementFormSchema = yup.object({
      disbursement_date: yup.date().required().label("Disbursement Date"),
      paid_amount: yup.number().positive().min(0.01).required().label("Paid Amount"),
      payment_method: yup.string().oneOf(["CASH", "BANK", "MOBILE_BANKING"]).required().label("Payment Method"),
      payment_acc_account_head_id: yup.number().positive().integer().required().label("Payment Account Head"),
      cheque_no: yup.string().nullable().max(30).label("Cheque No"),
      cheque_date: yup.date().nullable().label("Cheque Date"),
      mobileProvider: yup.string().nullable().when("payment_method", {
        is: "MOBILE_BANKING",
        then: (schema) => schema.required("Provider is required"),
        otherwise: (schema) => schema.nullable(),
      }),
      transactionId: yup.string().nullable().when("payment_method", {
        is: "MOBILE_BANKING",
        then: (schema) => schema.required("Transaction ID is required"),
        otherwise: (schema) => schema.nullable(),
      }),
      remarks: yup.string().nullable().max(255).label("Remarks"),
    });

    const {
      handleSubmit: handleDisburseSubmit,
      resetForm: resetDisburseForm,
    } = useForm({
      validationSchema: disbursementFormSchema,
    });

    const disbursement_date = useField("disbursement_date");
    const paid_amount = useField("paid_amount");
    const payment_method = useField("payment_method");
    const payment_acc_account_head_id = useField("payment_acc_account_head_id");
    const cheque_no = useField("cheque_no");
    const cheque_date = useField("cheque_date");
    const mobileProvider = useField("mobileProvider");
    const transactionId = useField("transactionId");
    const remarks = useField("remarks");

    return {
      submit,
      from_date,
      to_date,
      employee_id,
      loading,
      loadData,
      resetForm,
      handleDisburseSubmit,
      resetDisburseForm,
      disbursement_date,
      paid_amount,
      payment_method,
      payment_acc_account_head_id,
      cheque_no,
      cheque_date,
      mobileProvider,
      transactionId,
      remarks,
    };
  },
  methods: {
    formattedDate,
    formatCurrency(value) {
      return new Intl.NumberFormat("en-US", {
        minimumFractionDigits: 2,
        maximumFractionDigits: 2,
      }).format(value || 0);
    },
    getApproverName(item) {
      if (!item) return "N/A";
      // Check if approval process data is available in response
      if (item.approvalCurrentProcess) {
        const approval = item.approvalCurrentProcess;
        if (approval.approvedBy?.full_name) {
          return approval.approvedBy.full_name;
        }
        if (approval.approvedBy?.name) {
          return approval.approvedBy.name;
        }
        if (approval.matrixDetail?.designation?.name) {
          return approval.matrixDetail.designation.name;
        }
      }
      // Check approvalProcess array if available
      if (item.approvalProcess && Array.isArray(item.approvalProcess)) {
        const approvedProcess = item.approvalProcess.find(ap => ap.approved_ind === 1 || ap.approved_ind === true);
        if (approvedProcess) {
          if (approvedProcess.approvedBy?.full_name) {
            return approvedProcess.approvedBy.full_name;
          }
          if (approvedProcess.approvedBy?.name) {
            return approvedProcess.approvedBy.name;
          }
          if (approvedProcess.matrixDetail?.designation?.name) {
            return approvedProcess.matrixDetail.designation.name;
          }
        }
      }
      return "N/A";
    },
    getApprovalStepName(item) {
      if (!item) return "N/A";
      // Check if approval current process data is available (handle both camelCase and snake_case)
      const approvalCurrent = item.approvalCurrentProcess || item.approval_current_process;
      if (approvalCurrent) {
        const approvalStepName = approvalCurrent.approvalStepName || approvalCurrent.approval_step_name;
        if (approvalStepName) {
          return approvalStepName.name || approvalStepName.code || "N/A";
        }
      }
      // Check approvalProcess array if available
      const approvalProcess = item.approvalProcess || item.approval_process;
      if (approvalProcess && Array.isArray(approvalProcess)) {
        // Find the current pending process or the last approved process
        const currentProcess = approvalProcess.find(ap => ap.status === 'Pending' || ap.status === 'pending');
        if (currentProcess) {
          const stepName = currentProcess.approvalStepName || currentProcess.approval_step_name;
          if (stepName) {
            return stepName.name || stepName.code || "N/A";
          }
        }
        // If no pending, get the last approved one
        const approvedProcesses = approvalProcess.filter(ap => ap.approved_ind === 1 || ap.approved_ind === true);
        if (approvedProcesses.length > 0) {
          const lastApproved = approvedProcesses[approvedProcesses.length - 1];
          const stepName = lastApproved.approvalStepName || lastApproved.approval_step_name;
          if (stepName) {
            return stepName.name || stepName.code || "N/A";
          }
        }
      }
      return "N/A";
    },
    goTo(val) {
      this.$router.push({ path: val }).catch((err) => {});
    },
    viewRequisition(item) {
      this.$router.push({
        name: "view-money-requisition",
        params: { id: item.id },
      });
    },
    editItem(item) {
      this.$router.push({
        name: "add-money-requisition",
        query: { id: item.id },
      });
    },
    canEdit(item) {
      if (!item) return false;
      
      // Can only edit if status is 0 (draft/inactive)
      // Use loose equality to handle both string "0" and number 0
      const isDraft = item.status == 0;
      
      if (!isDraft) return false;
      
      // Check disbursed amount - can't edit if money has been disbursed
      // Handle null, undefined, 0, "0", etc.
      const disburseAmount = parseFloat(item.disburse_amount || 0);
      
      // Allow edit only if disbursed amount is 0 or NaN (null/undefined)
      return disburseAmount === 0 || isNaN(disburseAmount);
    },
    canDisburse(item) {
      if (!item) return false;
      
      // Can only disburse if approval status is Approved
      if (item.approval_status !== 'Approved') {
        return false;
      }
      
      // Can disburse if there's a due amount greater than 0
      const dueAmount = parseFloat(item.due_amount || 0);
      return dueAmount > 0;
    },
    openDisburseModal(item) {
      this.selectedRequisition = item;
      this.disburseDialog = true;
      
      // Set default values
      this.disbursement_date.value.value = new Date().toISOString().split('T')[0];
      this.paid_amount.value.value = parseFloat(item.due_amount || 0);
      this.payment_method.value.value = "CASH";
      this.payment_acc_account_head_id.value.value = null;
      this.cheque_no.value.value = null;
      this.cheque_date.value.value = null;
      this.mobileProvider.value.value = null;
      this.transactionId.value.value = null;
      this.remarks.value.value = null;
      
      // Load payment account heads for CASH
      this.loadPaymentAccountHeads("CASH");
    },
    closeDisburseModal() {
      this.disburseDialog = false;
      this.selectedRequisition = null;
      this.resetDisburseForm();
    },
    onPaymentMethodChange(value) {
      // Load payment account heads when payment method changes
      if (value) {
        this.loadPaymentAccountHeads(value);
        // Reset payment account head selection
        this.payment_acc_account_head_id.value.value = null;
        // Reset bank fields if switching from BANK
        if (value !== "BANK") {
          this.cheque_no.value.value = null;
          this.cheque_date.value.value = null;
        }
        // Reset mobile banking fields if switching from MOBILE_BANKING
        if (value !== "MOBILE_BANKING") {
          this.mobileProvider.value.value = null;
          this.transactionId.value.value = null;
        }
      }
    },
    loadPaymentAccountHeads(paymentMethod) {
      this.loadingAccountHeads = true;
      this.$store
        .dispatch("getPaymentAccountHeadList", { type: paymentMethod })
        .then((response) => {
          // Payment account heads loaded successfully
          // The store will update the getter automatically
        })
        .catch((error) => {
          console.error("Error loading payment account heads:", error);
          toast.error(error.response?.data?.message || "Failed to load payment account heads");
        })
        .finally(() => {
          this.loadingAccountHeads = false;
        });
    },
    submitDisbursement() {
      this.handleDisburseSubmit((values) => {
        // Validate paid amount doesn't exceed due amount
        const paidAmount = parseFloat(values.paid_amount);
        const dueAmount = parseFloat(this.selectedRequisition.due_amount || 0);
        
        if (paidAmount > dueAmount) {
          toast.error(`Paid amount cannot exceed due amount: ${this.formatCurrency(dueAmount)}`);
          return;
        }
        
        // Validate bank fields if payment method is BANK
        if (values.payment_method === "BANK") {
          if (!values.cheque_no || !values.cheque_no.trim()) {
            toast.error("Cheque No is required for Bank payment");
            return;
          }
          if (!values.cheque_date) {
            toast.error("Cheque Date is required for Bank payment");
            return;
          }
        }
        
        // Validate mobile banking fields if payment method is MOBILE_BANKING
        if (values.payment_method === "MOBILE_BANKING") {
          if (!values.mobileProvider) {
            toast.error("Provider is required for Mobile Banking payment");
            return;
          }
          if (!values.transactionId || !values.transactionId.trim()) {
            toast.error("Transaction ID is required for Mobile Banking payment");
            return;
          }
        }
        
        this.disbursing = true;
        
        const disbursementData = {
          payment_method: values.payment_method,
          payment_acc_account_head_id: values.payment_acc_account_head_id,
          disbursement_date: this.formattedDate(values.disbursement_date),
          paid_amount: paidAmount,
          remarks: values.remarks || null,
        };
        
        if (values.payment_method === "BANK") {
          disbursementData.cheque_date = this.formattedDate(values.cheque_date);
          disbursementData.cheque_no = values.cheque_no.trim();
        }
        
        if (values.payment_method === "MOBILE_BANKING") {
          disbursementData.mobileProvider = values.mobileProvider;
          disbursementData.transactionId = values.transactionId.trim();
        }
        
        this.$store
          .dispatch("disburseMoneyRequisition", {
            id: this.selectedRequisition.id,
            data: disbursementData,
          })
          .then((response) => {
            toast.success(response.data?.message || "Money requisition disbursed successfully");
            this.closeDisburseModal();
            this.loadData(); // Reload the list
          })
          .catch((error) => {
            toast.error(error.response?.data?.message || "Failed to disburse money requisition");
          })
          .finally(() => {
            this.disbursing = false;
          });
      })();
    },
    exportToPDF() {
      let serialNumber = 1;
      const columns = [
        "#SL",
        "Code",
        "Employee",
        "Purpose",
        "Total Amount",
        "Status",
      ];
      const rows = this.getSchema.map((item) => [
        serialNumber++,
        item.code,
        this.getEmployeeName(item.employee_id),
        item.purpose || "N/A",
        this.formatCurrency(item.total_amount),
        item.status == 1 ? "Active" : "Inactive",
      ]);
      this.$exportTableToPDF(
        columns,
        rows,
        "Money Requisition List",
        "money_requisition_list_" + this.$fileDate(),
        "landscape"
      );
    },
    exportToExcel() {
      var data = [
        [
          "#SL",
          "Code",
          "Employee",
          "Purpose",
          "Total Amount",
          "Status",
        ],
      ];

      this.getSchema.forEach((item, index) => {
        data.push([
          index + 1,
          item.code,
          item.employee?.name || "N/A",
          item.purpose || "N/A",
          item.total_amount,
          item.status == 1 ? "Active" : "Inactive",
        ]);
      });

      this.$exportTableToExcel(
        data,
        "Money Requisition List",
        "money_requisition_list_" + this.$fileDate()
      );
    },
    exportToCSV() {
      var data = [
        [
          "#SL",
          "Code",
          "Employee",
          "Purpose",
          "Total Amount",
          "Status",
        ],
      ];

      this.getSchema.forEach((item, index) => {
        data.push([
          index + 1,
          item.code,
          item.employee?.name || "N/A",
          item.purpose || "N/A",
          item.total_amount,
          item.status == 1 ? "Active" : "Inactive",
        ]);
      });

      this.$exportTableToCSV(
        data,
        "money_requisition_list_" + this.$fileDate()
      );
    },
  },
  beforeUnmount() {
    this.$store.commit("UNSET_MONEY_REQUISITION_LIST");
  },
};
</script>

<style scoped>
.cursor-pointer {
  cursor: pointer;
}
</style>
